package com.fantasytenis.web.screens.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.data.bracket.repository.BracketEntryWithPicks
import com.fantasytenis.shared.domain.bracket.entity.*
import com.fantasytenis.shared.domain.bracket.usecases.FullBracketData
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.bracket.*
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.*
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.borderTop
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Bracket Builder Screen V2 - Tree-based visualization with auto-save.
 *
 * Key features:
 * - Horizontal bracket tree view showing full tournament structure
 * - Auto-save on every pick with debouncing
 * - Visual feedback for save status
 * - Cascading pick confirmation when changing earlier round picks
 */
@Composable
fun BracketBuilderScreenV2(
    challengeId: String,
    entryId: String
) {
    val scope = rememberCoroutineScope()

    // Data state
    var bracketData by remember { mutableStateOf<FullBracketData?>(null) }
    var entryData by remember { mutableStateOf<BracketEntryWithPicks?>(null) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    // UI state
    var picks by remember { mutableStateOf<MutableMap<String, String>>(mutableMapOf()) }
    var showPlayerModal by remember { mutableStateOf<BracketMatchEntity?>(null) }
    // Default view mode - will be updated based on picks after loading
    var viewMode by remember { mutableStateOf<ViewMode?>(null) }
    var selectedRound by remember { mutableStateOf<RoundName?>(null) }
    var showCascadeConfirmation by remember { mutableStateOf<CascadeConfirmation?>(null) }

    // Auto-save hook
    val (saveState, triggerSave) = useAutoSavePicks(entryId)

    // Load bracket data
    LaunchedEffect(challengeId, entryId) {
        isLoading = true
        error = null

        // Load bracket structure
        DependencyContainer.getFullBracketUseCase(challengeId).fold(
            onSuccess = { data ->
                bracketData = data
                // Set initial round based on draw size
                selectedRound = data.rounds.firstOrNull()?.name
            },
            onFailure = { e ->
                error = e.message
            }
        )

        // Load existing entry picks
        DependencyContainer.getBracketEntryUseCase(entryId).fold(
            onSuccess = { data ->
                entryData = data
                // Pre-populate picks from existing entry
                val existingPicks = mutableMapOf<String, String>()
                data.picks.forEach { pick ->
                    existingPicks[pick.positionId] = pick.predictedWinnerId
                }
                console.log("Loaded ${data.picks.size} picks. By round: ${data.picks.groupBy { it.round }.mapValues { it.value.size }}")
                picks = existingPicks
                // Default to Guided View for all brackets
                if (viewMode == null) {
                    viewMode = ViewMode.GUIDED
                }
                isLoading = false
            },
            onFailure = { e ->
                error = e.message
                // Default to Guided View on error
                if (viewMode == null) {
                    viewMode = ViewMode.GUIDED
                }
                isLoading = false
            }
        )
    }

    // Calculate progress
    val totalMatches = bracketData?.rounds?.sumOf { it.matches.size } ?: 0
    val pickedMatches = picks.size
    @Suppress("UNUSED_VARIABLE")
    val progressPercent = if (totalMatches > 0) (pickedMatches.toDouble() / totalMatches * 100) else 0.0

    // Determine if view-only mode
    val isViewOnly = bracketData?.challenge?.isLocked == true ||
                     entryData?.entry?.isEditable == false

    // Handle pick selection with cascading logic
    val handleSelectWinner: (positionId: String, winnerId: String) -> Unit = { positionId, winnerId ->
        if (!isViewOnly) {
            val currentWinnerId = picks[positionId]
            console.log("handleSelectWinner: positionId=$positionId, winnerId=$winnerId, currentWinnerId=$currentWinnerId")

            if (currentWinnerId != null && currentWinnerId != winnerId) {
                // Check for dependent picks
                val dependentPicks = bracketData?.let { data ->
                    CascadingPicksCalculator.findDependentPicks(
                        currentPicks = picks,
                        changedPositionId = positionId,
                        previousWinnerId = currentWinnerId,
                        rounds = data.rounds
                    )
                } ?: emptyList()

                if (dependentPicks.isNotEmpty()) {
                    // Show confirmation dialog
                    showCascadeConfirmation = CascadeConfirmation(
                        positionId = positionId,
                        newWinnerId = winnerId,
                        dependentPositionIds = dependentPicks
                    )
                } else {
                    // No dependent picks, just update
                    val newPicks = picks.toMutableMap()
                    newPicks[positionId] = winnerId
                    picks = newPicks
                    triggerSave(newPicks)
                }
            } else if (currentWinnerId != winnerId) {
                // New pick or same pick
                val newPicks = picks.toMutableMap()
                newPicks[positionId] = winnerId
                picks = newPicks
                triggerSave(newPicks)
            }
        }
    }

    // Handle cascade confirmation
    val handleConfirmCascade: () -> Unit = {
        showCascadeConfirmation?.let { confirmation ->
            val newPicks = picks.toMutableMap()
            // Clear dependent picks
            confirmation.dependentPositionIds.forEach { posId ->
                newPicks.remove(posId)
            }
            // Set new pick
            newPicks[confirmation.positionId] = confirmation.newWinnerId
            picks = newPicks
            triggerSave(newPicks)
            showCascadeConfirmation = null
        }
    }

    Div({
        style {
            property("height", "var(--app-height)")
            backgroundColor(Colors.Background)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Header
        BracketBuilderHeader(
            challengeName = bracketData?.challenge?.name ?: "Bracket Builder",
            saveState = saveState,
            isViewOnly = isViewOnly,
            isComplete = pickedMatches == totalMatches,
            onBack = { NavigationState.navigate(Route.BracketChallengeDetail(challengeId)) },
            onSubmit = {
                if (pickedMatches == totalMatches) {
                    scope.launch {
                        forceImmediateSave(entryId, picks).fold(
                            onSuccess = {
                                NavigationState.navigate(Route.BracketChallengeDetail(challengeId))
                            },
                            onFailure = { /* Error handled by save state */ }
                        )
                    }
                }
            }
        )

        // Progress Bar
        BracketProgressBar(
            rounds = bracketData?.rounds ?: emptyList(),
            picks = picks,
            maxPoints = bracketData?.draw?.maxPoints ?: 0,
            showRoundDetails = viewMode == ViewMode.TREE
        )

        // View mode toggle and lock banner
        if (bracketData != null) {
            ViewModeToolbar(
                viewMode = viewMode,
                onViewModeChange = { viewMode = it }
            )
        }

        // Lock/Submitted Banner
        if (isViewOnly && !isLoading && bracketData != null) {
            LockBannerV2(
                isLocked = bracketData?.challenge?.isLocked == true,
                isManuallyUnlocked = bracketData?.challenge?.isManuallyUnlocked == true,
                isManuallyLocked = bracketData?.challenge?.isManuallyLocked == true,
                isSubmitted = entryData?.entry?.status == BracketEntryStatus.SUBMITTED
            )
        }

        // Manual Unlock Banner - show when challenge is manually unlocked and user can still submit
        if (!isViewOnly && !isLoading && bracketData?.challenge?.isManuallyUnlocked == true) {
            ManualUnlockBanner()
        }

        // Main Content
        Main({
            style {
                flex(1)
                overflow("hidden")
            }
        }) {
            when {
                isLoading -> LoadingIndicator()
                error != null -> ErrorMessage(error!!) {
                    scope.launch {
                        isLoading = true
                        error = null
                        DependencyContainer.getFullBracketUseCase(challengeId).fold(
                            onSuccess = { bracketData = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                bracketData != null -> {
                    // Use effective view mode (default to GUIDED while loading)
                    val effectiveViewMode = viewMode ?: ViewMode.GUIDED
                    when (effectiveViewMode) {
                        ViewMode.TREE -> {
                            BracketTreeContainer(
                                rounds = bracketData!!.rounds,
                                drawSize = bracketData!!.draw.drawSize,
                                picks = picks,
                                isViewOnly = isViewOnly,
                                onSelectWinner = handleSelectWinner,
                                onShowMatchDetails = { showPlayerModal = it }
                            )
                        }
                        ViewMode.GUIDED -> {
                            // Three-panel guided view for efficient pick-making
                            GuidedViewContainer(
                                rounds = bracketData!!.rounds,
                                drawSize = bracketData!!.draw.drawSize,
                                picks = picks,
                                isViewOnly = isViewOnly,
                                onSelectWinner = handleSelectWinner,
                                onShowMatchDetails = { showPlayerModal = it }
                            )
                        }
                        ViewMode.LIST -> {
                            // Fallback to round-by-round view
                            RoundByRoundView(
                                rounds = bracketData!!.rounds,
                                picks = picks,
                                isViewOnly = isViewOnly,
                                selectedRound = selectedRound ?: bracketData!!.rounds.first().name,
                                onSelectRound = { selectedRound = it },
                                onSelectWinner = handleSelectWinner,
                                onShowMatchDetails = { showPlayerModal = it }
                            )
                        }
                    }
                }
            }
        }

        // Player comparison modal
        showPlayerModal?.let { match ->
            PlayerComparisonModalV2(
                match = match,
                currentPick = picks[match.player1.id],
                isViewOnly = isViewOnly,
                onPick = { winnerId ->
                    handleSelectWinner(match.player1.id, winnerId)
                    showPlayerModal = null
                },
                onDismiss = { showPlayerModal = null }
            )
        }

        // Cascade confirmation dialog
        showCascadeConfirmation?.let { confirmation ->
            CascadeConfirmationDialog(
                dependentCount = confirmation.dependentPositionIds.size,
                onConfirm = handleConfirmCascade,
                onCancel = { showCascadeConfirmation = null }
            )
        }
    }
}

/**
 * View mode options.
 */
enum class ViewMode {
    GUIDED, // Three-panel guided view for making picks efficiently (default)
    TREE,   // Full bracket tree view
    LIST    // Round-by-round list view
}

/**
 * Data for cascade confirmation dialog.
 */
data class CascadeConfirmation(
    val positionId: String,
    val newWinnerId: String,
    val dependentPositionIds: List<String>
)

@Composable
private fun BracketBuilderHeader(
    challengeName: String,
    saveState: AutoSaveState,
    isViewOnly: Boolean,
    isComplete: Boolean,
    onBack: () -> Unit,
    onSubmit: () -> Unit
) {
    Header({
        style {
            flexShrink(0)
            backgroundColor(Colors.Primary)
            padding(12.px, 24.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        // Left: Back button and title
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
            }
        }) {
            Button(
                text = "Back",
                onClick = onBack,
                variant = "secondary"
            )
            H2({
                style {
                    color(Colors.White)
                    fontSize(18.px)
                    fontWeight(700)
                    margin(0.px)
                }
            }) {
                Text(challengeName)
            }
        }

        // Center: Save status
        InlineSaveStatus(saveState)

        // Right: Submit button
        if (!isViewOnly) {
            Button(
                text = "Submit Bracket",
                onClick = onSubmit,
                variant = if (isComplete) "primary" else "disabled"
            )
        }
    }
}

@Composable
private fun ViewModeToolbar(
    viewMode: ViewMode?,
    onViewModeChange: (ViewMode) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(8.px, 24.px)
            backgroundColor(Colors.Surface)
            borderBottom(1.px, LineStyle.Solid, Colors.Border)
        }
    }) {
        // View mode toggle
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(4.px)
            }
        }) {
            ViewModeButton(
                label = "Guided View",
                isSelected = viewMode == ViewMode.GUIDED,
                onClick = { onViewModeChange(ViewMode.GUIDED) }
            )
            ViewModeButton(
                label = "Tree View",
                isSelected = viewMode == ViewMode.TREE,
                onClick = { onViewModeChange(ViewMode.TREE) }
            )
            ViewModeButton(
                label = "List View",
                isSelected = viewMode == ViewMode.LIST,
                onClick = { onViewModeChange(ViewMode.LIST) }
            )
        }
    }
}

@Composable
private fun ViewModeButton(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(6.px, 12.px)
            borderRadius(6.px)
            cursor("pointer")
            backgroundColor(if (isSelected) Colors.Primary else Colors.SurfaceVariant)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            fontSize(13.px)
            fontWeight(if (isSelected) 600 else 400)
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LockBannerV2(
    isLocked: Boolean,
    isManuallyUnlocked: Boolean,
    isManuallyLocked: Boolean,
    isSubmitted: Boolean
) {
    val (bgColor, icon, message) = when {
        isManuallyLocked -> Triple(Colors.WarningLight, "\uD83D\uDD12", "Bracket locked by administrator")
        isLocked && !isManuallyUnlocked -> Triple(Colors.WarningLight, "\uD83D\uDD12", "Bracket locked - Tournament has started")
        isSubmitted -> Triple(Colors.SuccessLight, "\u2705", "Bracket submitted - View only")
        else -> Triple(Colors.InfoLight, "\uD83D\uDC41\uFE0F", "View only mode")
    }

    Div({
        style {
            backgroundColor(bgColor)
            padding(10.px, 24.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(10.px)
        }
    }) {
        Span({ style { fontSize(16.px) } }) { Text(icon) }
        Span({
            style {
                fontSize(13.px)
                color(Colors.TextPrimary)
            }
        }) {
            Text(message)
        }
    }
}

@Composable
private fun ManualUnlockBanner() {
    Div({
        style {
            backgroundColor(Colors.InfoLight)
            padding(10.px, 24.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(4.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
            }
        }) {
            Span({ style { fontSize(16.px) } }) { Text("\uD83D\uDD13") } // Unlocked padlock
            Span({
                style {
                    fontSize(13.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                }
            }) {
                Text("This bracket has been temporarily reopened by an administrator.")
            }
        }
        Span({
            style {
                fontSize(12.px)
                color(Colors.TextSecondary)
                marginLeft(26.px)
            }
        }) {
            Text("Submit your picks now - this may be reverted at any time.")
        }
    }
}

@Composable
private fun CascadeConfirmationDialog(
    dependentCount: Int,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    // Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "1000")
        }
        onClick { onCancel() }
    }) {
        // Dialog
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(12.px)
                padding(24.px)
                maxWidth(400.px)
                width(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Icon
            Div({
                style {
                    textAlign("center")
                    marginBottom(16.px)
                }
            }) {
                Span({
                    style {
                        fontSize(40.px)
                    }
                }) {
                    Text("\u26A0\uFE0F") // Warning emoji
                }
            }

            // Title
            H3({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    textAlign("center")
                    margin(0.px, 0.px, 12.px)
                }
            }) {
                Text("Reset Later Picks?")
            }

            // Message
            P({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    textAlign("center")
                    margin(0.px, 0.px, 24.px)
                    lineHeight("1.5")
                }
            }) {
                Text("Changing this pick will reset ")
                B { Text("$dependentCount") }
                Text(" later pick${if (dependentCount > 1) "s" else ""} that depend on your current selection.")
            }

            // Buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                }
            }) {
                Button(
                    text = "Cancel",
                    onClick = onCancel,
                    variant = "secondary"
                )
                Button(
                    text = "Continue",
                    onClick = onConfirm,
                    variant = "primary"
                )
            }
        }
    }
}

@Composable
private fun LoadingIndicator() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            height(100.percent)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorMessage(message: String, onRetry: () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            height(100.percent)
            padding(24.px)
        }
    }) {
        Span({
            style { fontSize(48.px) }
        }) {
            Text("\u274C") // X emoji
        }
        P({
            style {
                fontSize(16.px)
                color(Colors.Error)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun RoundByRoundView(
    rounds: List<BracketRoundEntity>,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    selectedRound: RoundName,
    onSelectRound: (RoundName) -> Unit,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit
) {
    Div({
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Round navigator
        RoundNavigatorWithProgress(
            rounds = rounds,
            picks = picks,
            selectedRound = selectedRound,
            onSelectRound = onSelectRound
        )

        // Matches for selected round
        val roundData = rounds.find { it.name == selectedRound }
        if (roundData != null) {
            Div({
                style {
                    flex(1)
                    overflow("auto")
                    padding(24.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                        gap(16.px)
                        maxWidth(1200.px)
                        margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    }
                }) {
                    roundData.matches.forEach { match ->
                        BracketMatchNode(
                            match = match,
                            selectedWinnerId = picks[match.player1.id],
                            isViewOnly = isViewOnly,
                            variant = MatchNodeVariant.FULL,
                            width = 300,
                            height = 100,
                            onSelectWinner = { winnerId ->
                                onSelectWinner(match.player1.id, winnerId)
                            },
                            onShowDetails = { onShowMatchDetails(match) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PlayerComparisonModalV2(
    match: BracketMatchEntity,
    currentPick: String?,
    isViewOnly: Boolean,
    onPick: (String) -> Unit,
    onDismiss: () -> Unit
) {
    // Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "1000")
        }
        onClick { onDismiss() }
    }) {
        // Modal content
        Div({
            style {
                backgroundColor(Colors.Surface)
                borderRadius(16.px)
                padding(24.px)
                maxWidth(600.px)
                width(90.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(24.px)
                }
            }) {
                H3({
                    style {
                        fontSize(20.px)
                        fontWeight(600)
                        margin(0.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text("${match.round.displayName} - Match ${match.matchNumber}")
                }

                Span({
                    style {
                        cursor("pointer")
                        fontSize(24.px)
                        color(Colors.TextSecondary)
                    }
                    onClick { onDismiss() }
                }) {
                    Text("\u00D7") // X close
                }
            }

            // Points info
            Div({
                style {
                    backgroundColor(Colors.Highlight.Background)
                    padding(12.px, 16.px)
                    borderRadius(8.px)
                    marginBottom(24.px)
                    textAlign("center")
                }
            }) {
                Text("This pick is worth ")
                B { Text("${match.round.basePoints} points") }
            }

            // Player comparison
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "1fr auto 1fr")
                    gap(16.px)
                    alignItems(AlignItems.Start)
                }
            }) {
                // Player 1 details
                PlayerDetailCardV2(
                    position = match.player1,
                    isSelected = currentPick == match.player1.playerId,
                    isViewOnly = isViewOnly,
                    onSelect = { if (!isViewOnly) match.player1.playerId?.let { onPick(it) } }
                )

                // VS
                Div({
                    style {
                        padding(40.px, 16.px)
                        fontSize(18.px)
                        fontWeight(600)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("VS")
                }

                // Player 2 details
                PlayerDetailCardV2(
                    position = match.player2,
                    isSelected = currentPick == match.player2.playerId,
                    isViewOnly = isViewOnly,
                    onSelect = { if (!isViewOnly) match.player2.playerId?.let { onPick(it) } }
                )
            }
        }
    }
}

@Composable
private fun PlayerDetailCardV2(
    position: DrawPositionEntity,
    isSelected: Boolean,
    isViewOnly: Boolean,
    onSelect: () -> Unit
) {
    val hasPlayer = position.playerId != null
    val isClickable = hasPlayer && !isViewOnly

    Div({
        style {
            backgroundColor(if (isSelected) Colors.SuccessLight else Colors.SurfaceVariant)
            borderRadius(12.px)
            padding(20.px)
            textAlign("center")
            cursor(if (isClickable) "pointer" else "default")
            border(
                3.px,
                LineStyle.Solid,
                if (isSelected) Colors.Success else Color("transparent")
            )
            property("transition", "all 0.2s")
            if (isViewOnly) {
                opacity(0.8)
            }
        }
        if (isClickable) {
            onClick { onSelect() }
        }
    }) {
        // Seed
        position.seed?.let { seed ->
            Div({
                style {
                    marginBottom(8.px)
                }
            }) {
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(14.px)
                        fontWeight(600)
                        padding(4.px, 12.px)
                        borderRadius(20.px)
                    }
                }) {
                    Text("Seed $seed")
                }
            }
        }

        // Name
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                margin(8.px, 0.px)
            }
        }) {
            Text(position.displayName)
        }

        // Country
        position.playerCountry?.let { country ->
            Div({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    marginBottom(16.px)
                }
            }) {
                Text(country)
            }
        }

        // Select button - only show if not view-only
        if (hasPlayer && !isViewOnly) {
            Button(
                text = if (isSelected) "Selected" else "Pick Winner",
                onClick = onSelect,
                variant = if (isSelected) "secondary" else "primary"
            )
        }
    }
}
